'use client';

import { useHover } from '@react-aria/interactions';
import type { ReactNode} from 'react';
import React, { useState } from 'react';
import { Arrow, useLayer } from 'react-laag';
import type { PlacementType } from 'react-laag/dist/PlacementType';
import styled from 'styled-components';

import { radiusTokens } from '../../theme/radius';

type Props = {
  content: ReactNode;
  placement?: PlacementType;
  open?: boolean;
  offset?: number;
  children: ReactNode;
  containerId?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'content'>;

export const Tooltip = (props: Props) => {
  const {
    placement = 'top-center',
    offset = 8,
    content,
    children,
    open = false,
    containerId,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(open);
  const { isHovered, hoverProps } = useHover({
    onHoverStart: () => setIsOpen(true),
    onHoverEnd: () => setIsOpen(false),
  });

  const containerElement = (containerId && document.getElementById(containerId)) || undefined;

  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen: isHovered,
    placement: placement,
    auto: true,
    triggerOffset: offset,
    container: containerElement,
  });

  return (
    <>
      <Wrapper {...rest} {...triggerProps} {...hoverProps}>
        {children}
      </Wrapper>
      {isOpen &&
        renderLayer(
          <div {...layerProps}>
            <TooltipStyle>{content}</TooltipStyle>
            {/*@ts-ignore*/}
            <Arrow {...arrowProps} backgroundColor="#000" size={6} />
          </div>,
        )}
    </>
  );
};

const Wrapper = styled.div`
  cursor: pointer;
`;

const TooltipStyle = styled.div`
  border-radius: var(${radiusTokens.md});
  padding: 1.2rem;
  z-index: 150;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  color: #ffffff;
`;
